import { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle, Loader2 } from 'lucide-react';
import { submitContactForm } from '../api/contact';

const CONTACT_PHONE_DISPLAY = '0532 179 93 21';
const CONTACT_PHONE_TEL = '+905321799321';
const CONTACT_EMAIL = 'info@maplio.com.tr';
const CONTACT_ADDRESS =
  'Esentepe Mh. Kasap Sk. Özen Apt. No:15 K:4 D:11, Şht. Ertuğrul Kabataş Cd., 34394 Şişli/İstanbul';

export default function Contact() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage('');

    try {
      await submitContactForm({
        fullName: formState.name.trim(),
        email: formState.email.trim(),
        phone: formState.phone.trim() || null,
        subject: formState.subject.trim(),
        message: formState.message.trim(),
        sourcePage: typeof window !== 'undefined' ? window.location.pathname || '/' : '/',
      });

      setIsSubmitted(true);
      setFormState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });

      window.setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Mesaj gönderilirken bir hata oluştu.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <section id="contact" className="section-padding">
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-semibold mb-4">
              İletişim
            </span>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-6 text-slate-900 dark:text-white">
              Bizimle <span className="gradient-text">İletişime Geçin</span>
            </h2>

            <p className="text-slate-600 dark:text-slate-400 mb-8 leading-relaxed text-lg">
              Maplio hakkında daha fazla bilgi almak, demo talep etmek veya özel çözümler için bize ulaşın.
              Ekibimiz en kısa sürede size dönüş yapacaktır.
            </p>

            <div className="space-y-6">
              <a href={`tel:${CONTACT_PHONE_TEL}`} className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Telefon</p>
                  <p className="font-medium text-slate-900 dark:text-white group-hover:text-primary transition-colors">
                    {CONTACT_PHONE_DISPLAY}
                  </p>
                </div>
              </a>

              <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-4 group">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-colors">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">E-posta</p>
                  <p className="font-medium text-slate-900 dark:text-white group-hover:text-primary transition-colors">
                    {CONTACT_EMAIL}
                  </p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Adres</p>
                  <p className="font-medium text-slate-900 dark:text-white">{CONTACT_ADDRESS}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-slate-100 dark:bg-slate-800 p-8 rounded-3xl border border-slate-200 dark:border-slate-700">
            <h3 className="text-2xl font-bold mb-6 text-slate-900 dark:text-white">Bize Mesaj Gönderin</h3>

            {isSubmitted ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
                <h4 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Mesajınız Gönderildi</h4>
                <p className="text-slate-500 dark:text-slate-400">En kısa sürede size dönüş yapacağız.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {errorMessage ? (
                  <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
                    {errorMessage}
                  </div>
                ) : null}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="name"
                    value={formState.name}
                    onChange={handleChange}
                    placeholder="Ad Soyad"
                    required
                    maxLength={120}
                    className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                  />
                  <input
                    type="email"
                    name="email"
                    value={formState.email}
                    onChange={handleChange}
                    placeholder="E-posta"
                    required
                    maxLength={120}
                    className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="tel"
                    name="phone"
                    value={formState.phone}
                    onChange={handleChange}
                    placeholder="Telefon (isteğe bağlı)"
                    maxLength={32}
                    className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                  />
                  <select
                    name="subject"
                    value={formState.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all appearance-none"
                  >
                    <option value="">Konu Seçin</option>
                    <option value="Demo Talebi">Demo Talebi</option>
                    <option value="Satış">Satış</option>
                    <option value="Teknik Destek">Teknik Destek</option>
                    <option value="İş Birliği">İş Birliği</option>
                    <option value="Diğer">Diğer</option>
                  </select>
                </div>

                <textarea
                  name="message"
                  value={formState.message}
                  onChange={handleChange}
                  placeholder="Mesajınız"
                  rows={4}
                  required
                  maxLength={3000}
                  className="w-full px-4 py-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all resize-none"
                />

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary py-4 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Gönder
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
